import React from 'react';
import Modal from 'src/shared/components/modal/Modal';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import useBoolean from 'src/shared/hooks/useBoolean';
import { Trash2 as DeleteOutlineIcon, FileText as InsertDriveFileOutlinedIcon } from 'lucide-react';
import { FileRow } from './FilesTable';

type DeleteFileModalProps = {
    file: FileRow;
    onClose: () => void;
    onDelete: () => Promise<void>;
};

const DeleteFileModal = ({ file, onClose, onDelete }: DeleteFileModalProps) => {
    const [isDeleting, deleting] = useBoolean(false);

    const handleDelete = async () => {
        deleting.on();
        try {
            await onDelete();
        } finally {
            deleting.off();
        }
    };

    return (
        <Modal onClose={onClose} dismissOnClickOutside={false}>
            <div className='w-[700px] max-w-[90vw]'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <div className='w-14 h-14 rounded-full border-2 border-teal-60 flex items-center justify-center'>
                        <DeleteOutlineIcon className='text-teal-60' sx={{ fontSize: 28 }} />
                    </div>
                    <h2 className='text-[28px] font-normal text-teal-60 m-0'>Delete file</h2>
                </div>

                {/* Confirmation message */}
                <p className='text-base text-navy mb-6'>Are you sure you want to delete the following file?</p>

                {/* File Info */}
                <div className='mb-8 p-4 bg-grey-0 rounded-md flex items-center gap-4'>
                    <InsertDriveFileOutlinedIcon className='text-grey-80' sx={{ fontSize: 48 }} />
                    <div className='flex-1'>
                        <p className='text-base font-medium text-navy mb-1'>{file.name}</p>
                        <p className='text-sm text-grey-60 mb-0'>{file.sizeLabel}</p>
                    </div>
                </div>

                {/* Actions */}
                <div className='flex justify-end gap-3'>
                    <SecondaryButton onClick={onClose} disabled={isDeleting} className='min-w-[140px] px-8 py-3 text-base'>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleDelete} disabled={isDeleting} className='min-w-[140px] px-8 py-3 text-base'>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default DeleteFileModal;
